import React, { useEffect } from 'react';
import {
  Drawer,
  Box,
  IconButton,
  useTheme,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { X, Video } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';
import { CreateBookingDTO } from '../../types/booking';
import { useCustomers } from '../../hooks/customer/useCustomers';
import ResourceSection from './BookingForm/sections/ResourceSection';
import CustomerSection from './BookingForm/sections/CustomerSection';
import ServiceSection from './BookingForm/sections/ServiceSection';
import TimeSection from './BookingForm/sections/TimeSection';
import RecurrenceSection from './BookingForm/sections/RecurrenceSection';
import { useAuth } from '../../hooks';
import { AUTH_CONSTANTS } from '../../config/constants';
import { useServices } from '../../hooks/service/useServices';
interface BookingFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateBookingDTO) => void;
  selectedSlot?: { start: string; end: string } | null;
  isSubmitting?: boolean;
}

const BookingForm: React.FC<BookingFormProps> = ({
  open,
  onClose,
  onSubmit,
  selectedSlot,
  isSubmitting = false,
}) => {
  const theme = useTheme();
  const { role, clientResourceId } = useAuth();
  const { customers } = useCustomers();

  const getDefaultResourceId = () => {
    // For regular users, always use their clientResourceId if available
    if (role === AUTH_CONSTANTS.ROLES.USER && clientResourceId) {
      return clientResourceId;
    }
    // For admins, don't set a default - let them choose
    return clientResourceId ? clientResourceId : 0;
  };

  const methods = useForm<CreateBookingDTO>({
    defaultValues: {
      resourceId: getDefaultResourceId(), //clientResourceId || 0,
      customerId: 0,
      serviceId: [],
      title: '',
      description: '',
      startTime: selectedSlot ? selectedSlot.start : '',
      endTime: selectedSlot ? selectedSlot.end : '',
      recurrenceRule: '',
    },
  });
  const { control, watch, reset, setValue } = methods;

  const selectedResourceId = watch('resourceId');
  const selectedServiceIds = watch('serviceId');
  const { services } = useServices();

  // Check if any selected service is an online meeting service
  const onlineMeetingService = services.find(
    s => selectedServiceIds?.includes(s.id) && s.meetingProvider && s.meetingProvider !== 'none'
  );
  const meetingProviderLabel =
    onlineMeetingService?.meetingProvider === 'zoom'
      ? 'Zoom'
      : onlineMeetingService?.meetingProvider === 'google_meet'
        ? 'Google Meet'
        : '';

  useEffect(() => {
    if (
      role === AUTH_CONSTANTS.ROLES.USER &&
      clientResourceId &&
      selectedResourceId !== clientResourceId
    ) {
      setValue('resourceId', clientResourceId);
    }
  }, [clientResourceId, selectedResourceId, setValue, role]);

  useEffect(() => {
    if (selectedSlot) {
      reset({
        title: '',
        description: '',
        startTime: selectedSlot.start,
        endTime: selectedSlot.end,
        recurrenceRule: '',
        resourceId: getDefaultResourceId(),
      });
    }
  }, [selectedSlot, reset, clientResourceId, role]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          p: 0,
          py: { xs: 2, sm: 2 },
          px: { xs: 2, sm: 3 },
        },
      }}
    >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(onSubmit)}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DialogTitle>Create New Booking</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <ResourceSection control={control} disabled={isSubmitting} />
              <CustomerSection control={control} customers={customers} disabled={isSubmitting} />
              <ServiceSection
                control={control}
                resourceId={selectedResourceId}
                disabled={isSubmitting}
              />
              <TimeSection control={control} disabled={isSubmitting} />
              <RecurrenceSection control={control} disabled={isSubmitting} />
              {onlineMeetingService && (
                <Alert severity="info" icon={<Video size={18} />} sx={{ mt: 1 }}>
                  This is an online service. A {meetingProviderLabel} meeting link will be
                  automatically created when the booking is confirmed.
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Creating...' : 'Create Booking'}
            </Button>
          </DialogActions>
        </Box>
      </FormProvider>
    </Drawer>
  );
};

export default BookingForm;
